// STEP 3, `/comps` — THE APP'S "COMPARABLE SALES" (WEB-PARITY-3, 3B: CP-1 to CP-15, AN-1).
//
// The app's screen (`CompsView`, AppealAce/Features/Comps/CompsView.swift) laid out for a wide screen (ruling D4: the
// app's cards replace the website's sortable table): the hero; "Find comps" with the explicit "Auto-search comparable
// sales" and its four progress lines; the "ranked the N closest" line; "YOUR COMPS", the valuation-date banner and the
// thin-market note; one card per comp; "Add a comp yourself" and its sheet (`CompEditView`); "Where to find recent
// sales" once a search came back empty; and the bottom bar the server's NEW-21 decides. Every word is the app's
// (`parity3Wording.ts`, pinned to its Swift line) or the server's.
//
// THE NARROWING IS HERE NOW (AN-1): include and exclude are the sheet's "Include in appeal", a sale the owner knows is
// "Add a comp yourself", and the app's long-press "Remove" is a button on the card. `/analyze` keeps only what the app
// shows on its Value step, until Batch 4.
//
// WHAT THE SERVER DECIDES AND THIS PAGE ONLY PRINTS: which comps there are and whether each is usable (`v2SearchComps`);
// whether Continue is open (NEW-21); every card's match score, recency, time and size lines and flags, and the
// valuation date (`v2Value` `compute`, `value/compCards.ts`); what a comp address's county record says (`lookup`).
// THE ONE SEARCH RUNS BY ITSELF THE FIRST TIME AND NEVER AGAIN (Tyler, 2026-10-21, "A": "search automatically the first
// time. The first time someone reaches Comps, the search starts by itself and they land on their sales — the same single
// search they'd press the button for, so no extra cost. On later visits it just opens their saved comps"). This reverses
// the website's CP-2 (the app's explicit button, never a search on arrival): an owner who came from Condition saw a
// "searching" checklist that was only a READ of the stored set, then an empty page, then pressed the button for the real
// search. Now: on the FIRST arrival only — no search spent, no comps on file, and the page not opened for "add a sale" —
// `CompsStep` asks `v2SearchComps` `search` once and shows the designed wait (`CompsLoading`) while it runs. Every other
// arrival reads the stored set with an empty `edit`, under the app's plain "Reading your comparable sales…". The server's
// SEARCH_CAP still bounds a credit to one search, so the automatic one cannot cost a second. The button stays for a
// search that failed, with its own `ProgressLines`. The app has the same change in its next update (not in this batch).

import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type {
  AppealSnapshot, CompLookupResponse, CompView, SearchCompsEditInput, SearchCompsResponse, ValueResponse,
} from "@contract";
import { useAppealId, useSession } from "../../app/session";
import { searchSpent } from "../../app/snapshot";
import { AppAlert } from "../../components/AppAlert";
import { AddressCombobox } from "../../components/AddressCombobox";
import { CopyNote, DegradedList } from "../../components/copy";
import { PaidRoute } from "../../components/PaidRoute";
import { Refusal } from "../../components/Refusal";
import type { V2Failure } from "../../transport/types";
import { Card, Said, Sheet, StepHero } from "./parity3Parts";
import { Icon } from "./parity4Parts";
import {
  EMPTY_DRAFT, candidatesReference, compsLoadingTiming, dollars, draftOf, isUnitInBuilding, monthYear, oneDecimal,
  searchResult, searchTiming, sheetSavable, sheetWarnings, sizeSentence, soldSentence, sqftLabel, subjectBasisOf,
  timeAdjustedSentence, wideDate, type SearchResult, type SheetDraft,
} from "./parity3Rules";
import { say, sayWith, type Parity3WordingId } from "./parity3Wording";

type CardFacts = ValueResponse["body"]["value"]["comps"][number];
type SheetState = { mode: "add" } | { mode: "edit"; comp: CompView } | null;
/** Who started the search that is out: the first arrival (Tyler, 2026-10-21, "A") or the owner's button. */
type SearchVia = "arrival" | "button";

const PROGRESS: readonly Parity3WordingId[] = [
  "comps.search.progress.1", "comps.search.progress.2", "comps.search.progress.3", "comps.search.progress.4",
];

export function Comps() {
  const session = useSession();
  const appealId = useAppealId();
  return (
    <PaidRoute<SearchCompsResponse>
      // THE STORED SET, READ. An empty `edit` changes nothing and answers the set, NEW-21 and the last search's own
      // counts. It is a READ, so it says so, in the app's own words (`loading.compsReread`, AnalyzeView.swift:294) and
      // not as the "searching" checklist, which it used to borrow (Tyler, 2026-10-21: the page "acted like it was
      // generating the comps" and then opened empty). The first arrival's search is `CompsStep`'s, below.
      probe={() => session.transport.v2SearchComps.edit({ appealId })}
      deps={[session.transport, appealId, session.revision]}
      pending="loading.compsReread"
    >
      {(response, appeal) => <CompsStep first={response} firstAppeal={appeal} />}
    </PaidRoute>
  );
}

function CompsStep({ first, firstAppeal }: { first: SearchCompsResponse; firstAppeal: AppealSnapshot }) {
  const session = useSession();
  const appealId = useAppealId();
  const navigate = useNavigate();
  const [latest, setLatest] = useState(first);
  const appeal = latest.appeal ?? firstAppeal;
  const subject = appeal.subject;
  // WEB-PARITY-5: the Letter step's "Add a comparable sale" (GenerateView.swift:484, :500, :504) opens this step's own
  // add-a-sale sheet, as the app's opens `CompEditView` over Generate.
  const location = useLocation();
  const addSaleArrival = (location.state as { addSale?: unknown } | null)?.addSale === true;
  const [sheet, setSheet] = useState<SheetState>(() => (addSaleArrival ? { mode: "add" } : null));
  // THE FIRST ARRIVAL SEARCHES BY ITSELF (Tyler, 2026-10-21, "A"), decided once from the first answer: the credit's one
  // search is unspent, no sale is on file, and the owner did not come to add a sale. The server's own answer decides the
  // first two, so a reload or a step back after the search reads the saved sales instead.
  const [dueOnArrival] = useState(() => !searchSpent(firstAppeal) && first.body.comps.length === 0 && !addSaleArrival);
  // Which search is out: the arrival's (the designed wait stands in for the step) or the owner's button (its own lines).
  const [searching, setSearching] = useState<SearchVia | null>(dueOnArrival ? "arrival" : null);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [failure, setFailure] = useState<V2Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState<"match" | "board" | null>(null);
  const [cards, setCards] = useState<ValueResponse | null>(null);
  const [cardsTick, setCardsTick] = useState(0);

  const comps = latest.body.comps;

  // SEARCH_CAP's own condition on the server's own counters, read where every step reads it (`app/snapshot.ts`).
  const searched = searchSpent(appeal);
  const new21 = latest.decisions["NEW-21"];
  const blocked = new21.ran && new21.outcome === "block";
  const searchedEmpty = new21.ran && new21.outcome === "info";
  const usable = comps.filter((c) => c.usable).length;

  // THE CARDS' FACTS (CP-6 to CP-9): `compute` over the stored set, asked again after every change. Its refusal before
  // any comp exists is the server's precondition, so it is not asked then.
  useEffect(() => {
    if (comps.length === 0) {
      setCards(null);
      return;
    }
    let live = true;
    void session.transport.v2Value.compute({ appealId }).then((r) => {
      if (live) setCards(r.ok ? r.response : null);
    });
    return () => {
      live = false;
    };
  }, [cardsTick, comps.length, session.transport, appealId]);

  const factsOf = (compId: string): CardFacts | null => cards?.body.value.comps.find((c) => c.compId === compId) ?? null;
  const valuationDate = cards?.body.value.valuationDate ?? null;

  async function edit(input: Omit<SearchCompsEditInput, "appealId">): Promise<SearchCompsResponse | null> {
    setBusy(true);
    const r = await session.transport.v2SearchComps.edit({ appealId, ...input });
    setBusy(false);
    if (!r.ok) {
      setFailure(r);
      return null;
    }
    setFailure(null);
    setLatest(r.response);
    setCardsTick((t) => t + 1);
    return r.response;
  }

  async function autoSearch(via: SearchVia) {
    setSearching(via);
    setResult(null);
    setFailure(null);
    const started = Date.now();
    const r = await session.transport.v2SearchComps.search({ appealId });
    const elapsed = Date.now() - started;
    if (elapsed < searchTiming.minimumMs) await new Promise((done) => setTimeout(done, searchTiming.minimumMs - elapsed));
    setSearching(null);
    if (!r.ok) {
      setFailure(r);
      return;
    }
    setLatest(r.response);
    setResult(searchResult(r.response.body.meta, isUnitInBuilding(subject?.propertyType?.value)));
    setCardsTick((t) => t + 1);
  }

  // THE ONE AUTOMATIC SEARCH, ASKED FOR ONCE. The ref is what makes it once: StrictMode runs this effect twice on a fresh
  // mount (same instance, same ref), and a second `search` would be a second call for the credit's one. A failure leaves the
  // step as a failed button search does — the server's refusal and the button — and a later arrival asks again for as long
  // as the credit's search is unspent (`searchSpent`).
  const arrivalAsked = useRef(false);
  useEffect(() => {
    if (!dueOnArrival || arrivalAsked.current) return;
    arrivalAsked.current = true;
    void autoSearch("arrival");
  }, [dueOnArrival]);

  const county = subject?.county?.trim() ?? "";

  // WHILE THE ARRIVAL'S SEARCH RUNS the designed wait Tyler approved (2026-10-14) stands in for the step. A search the owner
  // starts with the button keeps `ProgressLines` in its card below.
  if (searching === "arrival") return <CompsLoading />;

  return (
    <div className="comps-step">
      <StepHero icon="house" titleId="comps.hero.title" bodyId="comps.hero.body" />

      <Card titleId="comps.search.title" accent data-card="search" footer={<Said id="comps.search.footer" as="p" />}>
        {searching === "button" ? (
          <ProgressLines />
        ) : !searched ? (
          <button type="button" className="btn primary search-button" data-held-copy="comps.search.button" onClick={() => void autoSearch("button")}>
            {say("comps.search.button")}
          </button>
        ) : (
          <div className="search-complete" data-search="complete">
            <span className="seal" aria-hidden="true">✓</span>
            <div>
              <Said id="comps.search.complete" as="b" />
              <Said id="comps.search.completeBody" as="p" />
            </div>
          </div>
        )}
        {result !== null && (
          <p className={`search-result ${result.tone}`} role="status" data-search-result={result.tone}>{result.text}</p>
        )}
        {failure !== null && <FailureLine failure={failure} context={say("comps.search.title")} />}
      </Card>

      {latest.body.meta.ranked > 0 && (
        <p className="candidates" data-candidates>
          <span className="seal" aria-hidden="true">✓</span>
          {candidatesReference(latest.body.meta.ranked, latest.body.meta.candidatesConsidered)}
        </p>
      )}

      {comps.length > 0 && (
        <>
          <div className="comps-header">
            <Said id="comps.header" as="b" />
            <Said id="comps.header.usable" values={{ n: usable }} />
          </div>
          {valuationDate !== null && (
            <div className="valuation-banner" data-valuation-date={valuationDate}>
              <Said id="comps.boardInfo.banner" as="p" values={{ date: wideDate(valuationDate) }} />
              <button type="button" className="link-button" data-held-copy="comps.boardInfo.link" onClick={() => setAlert("board")}>
                {say("comps.boardInfo.link")}
              </button>
            </div>
          )}
          {cards?.body.value.recencySkewsOld === true && <Said id="comps.thinMarket" as="p" className="thin-market" />}
        </>
      )}

      {comps.length === 0 ? (
        <Card titleId="comps.empty.title" data-card="no-comps">
          <Said id="comps.empty.body" as="p" />
          {searchedEmpty && <Said id="comps.empty.creditApplied" as="p" className="credit-applied" />}
        </Card>
      ) : (
        <ul className="comp-cards">
          {comps.map((comp) => (
            <CompCard
              key={comp.compId}
              comp={comp}
              facts={factsOf(comp.compId)}
              valuationDate={valuationDate}
              subjectSqft={subject?.sqft?.value ?? null}
              disabled={busy}
              onOpen={() => setSheet({ mode: "edit", comp })}
              onRemove={() => void edit({ remove: [comp.compId] })}
              onExplainMatch={() => setAlert("match")}
            />
          ))}
        </ul>
      )}

      <button type="button" className="add-comp" data-held-copy="comps.add" disabled={busy} onClick={() => setSheet({ mode: "add" })}>
        <span aria-hidden="true">＋</span> {say("comps.add")}
      </button>

      {searchedEmpty && (
        <section className="resources" data-card="sales-resources">
          <Said id="comps.resources.title" as="b" />
          <Said id="comps.resources.lead" as="p" />
          <ul>
            <li>{county !== ""
              ? <Said id="comps.resources.county" values={{ county }} />
              : <Said id="comps.resources.countyUnnamed" />}</li>
            <li><Said id="comps.resources.listing" /></li>
            <li><Said id="comps.resources.agent" /></li>
          </ul>
          <Said id="comps.resources.footer" as="p" />
        </section>
      )}

      <DegradedList degraded={latest.degraded} />

      <div className="step-footer centered" data-new21={new21.ran ? new21.outcome : "notRun"} data-copy-code={new21.ran ? new21.copy?.code : undefined}>
        {blocked && <Said id="comps.helper.addFirst" as="p" className="requirement" />}
        {searchedEmpty && <Said id="comps.helper.searchedEmpty" as="p" className="requirement" />}
        <button
          className="btn primary"
          type="button"
          disabled={blocked || busy || searching !== null}
          data-held-copy={usable > 0 ? "comps.continue.analyze" : searchedEmpty ? "comps.continue.withoutComps" : "comps.continue.addFirst"}
          onClick={() => navigate("/analyze")}
        >
          {say(usable > 0 ? "comps.continue.analyze" : searchedEmpty ? "comps.continue.withoutComps" : "comps.continue.addFirst")}
        </button>
      </div>

      {sheet !== null && (
        <CompSheet
          key={sheet.mode === "edit" ? sheet.comp.compId : "add"}
          start={sheet.mode === "edit" ? draftOf(sheet.comp) : EMPTY_DRAFT}
          subjectBasis={subjectBasisOf(subject)}
          valuationDate={valuationDate}
          onCancel={() => setSheet(null)}
          onSave={async (draft) => {
            const ok = sheet.mode === "edit" ? await saveEdit(sheet.comp, draft) : await saveNew(draft);
            if (ok) setSheet(null);
          }}
        />
      )}

      {alert === "match" && (
        <AppAlert
          title={say("comps.matchInfo.title")}
          titleId="comps.matchInfo.title"
          kind="match-info"
          buttons={[{ label: say("comps.gotIt"), wordsId: "comps.gotIt", cancel: true, onPress: () => setAlert(null) }]}
        >
          <Said id="comps.matchInfo.message" as="p" />
        </AppAlert>
      )}
      {alert === "board" && (
        <AppAlert
          title={say("comps.boardInfo.title")}
          titleId="comps.boardInfo.title"
          kind="board-info"
          buttons={[{ label: say("comps.gotIt"), wordsId: "comps.gotIt", cancel: true, onPress: () => setAlert(null) }]}
        >
          <Said id="comps.boardInfo.message" as="p" />
        </AppAlert>
      )}
    </div>
  );

  /** A new comp (`upsertComp` of `Comp(ownerProvided: true)`): added, then unticked if the sheet's toggle was off — its id
   *  is the server's, so it can only be named once it exists. */
  async function saveNew(draft: SheetDraft): Promise<boolean> {
    const before = new Set(comps.map((c) => c.compId));
    const added = await edit({
      ownerComps: [{
        address: draft.address,
        salePrice: { basis: "market", amount: draft.salePrice ?? 0 },
        ...(draft.hasSaleDate && draft.saleDate !== "" ? { saleDate: draft.saleDate } : {}),
        ...(draft.sqft === null ? {} : { sqft: draft.sqft }),
        ...(draft.beds === null ? {} : { beds: draft.beds }),
        ...(draft.baths === null ? {} : { baths: draft.baths }),
        ...(draft.distanceMi === null ? {} : { distanceMi: draft.distanceMi }),
        ...(draft.yearBuilt === null ? {} : { yearBuilt: draft.yearBuilt }),
      }],
    });
    if (added === null) return false;
    const fresh = added.body.comps.find((c) => !before.has(c.compId));
    if (fresh !== undefined && !draft.included) return (await edit({ exclude: [fresh.compId] })) !== null;
    return true;
  }

  /** A comp already in the set: every field the sheet holds, absent where it is empty only if it was empty before —
   *  an emptied field is sent as null, which clears it (the server's own rule). */
  async function saveEdit(comp: CompView, draft: SheetDraft): Promise<boolean> {
    const r = await edit({
      updates: [{
        compId: comp.compId,
        address: draft.address,
        salePrice: draft.salePrice === null ? null : { basis: "market", amount: draft.salePrice },
        saleDate: draft.hasSaleDate && draft.saleDate !== "" ? draft.saleDate : null,
        sqft: draft.sqft,
        beds: draft.beds,
        baths: draft.baths,
        distanceMi: draft.distanceMi,
        ...(draft.yearBuilt === null ? {} : { yearBuilt: draft.yearBuilt }),
      }],
      ...(draft.included === comp.included ? {} : draft.included ? { include: [comp.compId] } : { exclude: [comp.compId] }),
    });
    return r !== null;
  }
}

/**
 * STEP 3 WHILE ITS AUTOMATIC SEARCH RUNS — the designed wait Tyler approved on launch day (2026-10-14: "Yes to the comps
 * loading design but make sure to use the correct font"), in place of the one italic line it was (`loading.comps`). It
 * stood in for the step's first READ of the stored set until 2026-10-21, when the read became a plain line and this wait
 * moved to the search it describes (Tyler, "A").
 *
 * The Analyze step's language, and its parts: the step hero's tinted disc (here the app's magnifying glass) over the
 * app's title for this card, "Find comps" (CV:301); then one card, the Analyze card's (`card4`), holding a thin
 * amber bar (the letter overlay's track, `gen-progress-track`) and the app's four lines (CV:313-316) as a checklist — a
 * done line takes the emerald tick, the current line the site's spinner, a later line is muted. Under them, the card's
 * own footer on the step (CV:304), which is true on the website: the search runs once per appeal (the server's
 * SEARCH_CAP), and comps are added and removed on this step. No new words; the type is the site's own (Poppins, from
 * `--font` on the body), its sizes the classes'.
 *
 * Lines 1 to 3 advance on a timer; line 4 stays current until the search answers, and then `CompsStep` renders the step.
 * With reduced motion the spinner and the bar stand still (app.css) and the lines still advance. A screen reader hears one
 * polite status region, the current line; the checklist itself is drawn for the eye only.
 */
function CompsLoading() {
  const [at, setAt] = useState(0);
  const last = PROGRESS.length - 1;
  useEffect(() => {
    if (at >= last) return;
    const timer = setTimeout(() => setAt((n) => Math.min(n + 1, last)), compsLoadingTiming.lineMs);
    return () => clearTimeout(timer);
  }, [at, last]);
  return (
    <div className="comps-step comps-loading" data-comps-loading={at + 1}>
      <StepHero icon="search" titleId="comps.search.title" />
      <section className="card4 comps-loading-card">
        <span className="gen-progress-track thin" aria-hidden="true">
          <span className="gen-progress-fill" style={{ width: `${((at + 1) / (PROGRESS.length + 1)) * 100}%` }} />
        </span>
        <ol className="search-checklist" aria-hidden="true">
          {PROGRESS.map((id, i) => {
            const line = i < at ? "done" : i === at ? "now" : "later";
            return (
              <li key={id} className={line} data-line={line}>
                <span className="search-checklist-mark">
                  {line === "done" ? <Icon name="tick" size={16} /> : line === "now" ? <span className="spinner" /> : null}
                </span>
                <span data-held-copy={id}>{say(id)}</span>
              </li>
            );
          })}
        </ol>
        <p className="visually-hidden" role="status" aria-live="polite" data-held-copy={PROGRESS[at]} data-comps-loading-status>
          {say(PROGRESS[at])}
        </p>
        <Said id="comps.search.footer" as="p" className="card4-footer" />
      </section>
    </div>
  );
}

/** The app's `ProcessingStatusBar` over its four lines (CompsView.swift:310-318), one at a time. */
function ProgressLines() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setI((n) => (n + 1) % PROGRESS.length), 1200);
    return () => clearInterval(timer);
  }, []);
  return (
    <p className="search-progress" role="status" data-held-copy={PROGRESS[i]}>
      <span className="spinner" aria-hidden="true" />
      {say(PROGRESS[i])}
    </p>
  );
}

/** A refusal's own sentence, in the card it belongs to — the server's copy, never a client's. */
function FailureLine({ failure, context }: { failure: V2Failure; context: string }) {
  if (failure.kind === "v2Error") {
    return (
      <div data-error-code={failure.error.error.code}>
        <CopyNote copy={failure.error.error.copy} tone="warn" />
      </div>
    );
  }
  return <Refusal failure={failure} context={context} />;
}

// ── the card (CompsView.swift:842-1087) ─────────────────────────────────────────────────────────────────────────────

function CompCard({
  comp, facts, valuationDate, subjectSqft, disabled, onOpen, onRemove, onExplainMatch,
}: {
  comp: CompView;
  facts: CardFacts | null;
  valuationDate: string | null;
  subjectSqft: number | null;
  disabled: boolean;
  onOpen: () => void;
  onRemove: () => void;
  onExplainMatch: () => void;
}) {
  const address = comp.address.trim();
  return (
    <li className={comp.included ? "comp-card" : "comp-card out"} data-comp-id={comp.compId} data-source={comp.source}>
      <button type="button" className="comp-card-open" onClick={onOpen} disabled={disabled} aria-label={address === "" ? say("card.noAddress") : address} />
      <div className="comp-card-head">
        <b className="comp-address" data-held-copy={address === "" ? "card.noAddress" : undefined}>
          {address === "" ? say("card.noAddress") : address}
        </b>
        {comp.salePrice !== null && <span className="comp-price">{dollars(comp.salePrice.amount)}</span>}
      </div>
      {comp.source === "owner" && <Said id="card.ownerProvided" className="owner-chip" />}

      {facts?.match != null && (
        <div className="match-row" data-match={facts.match}>
          <span className="match-label">
            <Said id="card.match" />
            <button type="button" className="match-help" aria-label={say("comps.matchInfo.title")} onClick={onExplainMatch}>?</button>
          </span>
          <span className="match-bar" aria-hidden="true"><span style={{ width: `${facts.match}%` }} /></span>
          <span className="match-score" aria-label={sayWith("card.matchLabel", { score: facts.match })}>{facts.match}</span>
        </div>
      )}

      <ul className="pills">
        {comp.saleDate !== null && <li>{monthYear(comp.saleDate)}</li>}
        {comp.sqft !== null && <li>{sqftLabel(comp.sqft, subjectSqft)}</li>}
        {comp.beds !== null && <li>{sayWith("card.beds", { beds: Math.trunc(comp.beds) })}</li>}
        {comp.baths !== null && <li>{sayWith("card.baths", { baths: oneDecimal(comp.baths) })}</li>}
        {comp.distanceMi !== null && <li>{sayWith("card.distance", { miles: oneDecimal(comp.distanceMi) })}</li>}
      </ul>

      {facts?.recency != null && valuationDate !== null && (
        <div className="recency" data-recency={facts.recency.band}>
          <Said id={`card.recency.${facts.recency.band}` as Parity3WordingId} className={`recency-badge ${facts.recency.band}`} />
          <p>{soldSentence(facts.recency, valuationDate)}</p>
        </div>
      )}

      {facts?.timeAdjusted != null && <p className="comp-line">{timeAdjustedSentence(facts.timeAdjusted)}</p>}
      {facts?.timeAdjustmentGap === true && <Said id="card.timeGap" as="p" className="comp-line" />}

      {facts?.sizeLine != null && (
        <p className={facts.sizeLine.kind.startsWith("notSized") ? "comp-line caution" : "comp-line"} data-size-line={facts.sizeLine.kind}>
          {sizeSentence(facts.sizeLine)}
        </p>
      )}
      {facts?.largeSizeAdjustmentPercent != null && (
        <Said id="card.size.large" as="p" className="comp-line" values={{ pct: `${facts.largeSizeAdjustmentPercent}%` }} />
      )}
      {facts?.atOrAboveImplied === true && <Said id="card.aboveImplied" as="p" className="comp-line" />}

      {comp.salePrice === null
        ? <Said id="card.needsPrice" as="p" className="needs-price" />
        : !comp.included && <Said id="card.excluded" as="p" className="excluded" />}

      <button type="button" className="comp-remove" data-held-copy="comps.remove" disabled={disabled} onClick={onRemove}>
        {say("comps.remove")}
      </button>
    </li>
  );
}

// ── the add/edit sheet (CompEditView.swift) ─────────────────────────────────────────────────────────────────────────

function CompSheet({
  start, subjectBasis, valuationDate, onCancel, onSave,
}: {
  start: SheetDraft;
  subjectBasis: number | null;
  valuationDate: string | null;
  onCancel: () => void;
  onSave: (draft: SheetDraft) => Promise<void>;
}) {
  const session = useSession();
  const appealId = useAppealId();
  const [draft, setDraft] = useState<SheetDraft>(start);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupLine, setLookupLine] = useState<{ text: string; code: string } | null>(null);
  const saving = useRef(false);
  const set = (patch: Partial<SheetDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const warnings = sheetWarnings(draft, subjectBasis, valuationDate);

  async function lookUp() {
    setLookingUp(true);
    setLookupLine(null);
    const r = await session.transport.v2SearchComps.lookup({ appealId, address: draft.address.trim() });
    setLookingUp(false);
    if (!r.ok) {
      if (r.kind === "v2Error") setLookupLine({ text: r.error.error.copy.text, code: r.error.error.copy.code });
      return;
    }
    const found: CompLookupResponse["body"]["lookup"] = r.response.body.lookup;
    // ONLY THE EMPTY FIELDS, NEVER OVERWRITING what the owner typed (CompEditView.swift:60-61, :103-111).
    if (found.outcome === "filled") {
      setDraft((d) => ({
        ...d,
        sqft: d.sqft ?? found.sqft,
        beds: d.beds ?? found.beds,
        baths: d.baths ?? found.baths,
        yearBuilt: d.yearBuilt ?? found.yearBuilt,
        distanceMi: d.distanceMi ?? found.distanceMi,
      }));
    }
    setLookupLine({ text: found.copy.text, code: found.copy.code });
  }

  return (
    <Sheet
      titleId={draft.address.trim() === "" ? "sheet.title.add" : "sheet.title.edit"}
      kind="comp"
      cancel={{ id: "sheet.cancel", onPress: onCancel }}
      confirm={{
        id: "sheet.save",
        disabled: !sheetSavable(draft),
        onPress: async () => {
          if (saving.current) return;
          saving.current = true;
          await onSave(draft);
          saving.current = false;
        },
      }}
    >
      <section className="sheet-section">
        <Said id="sheet.property" as="h3" />
        <AddressCombobox
          suggester={session.addressSuggester}
          label={say("sheet.address")}
          placeholder=""
          value={draft.address}
          onChange={(address) => set({ address })}
        />
        <NumberRow labelId="sheet.salePrice" prefixId="sheet.dollar" value={draft.salePrice} onChange={(salePrice) => set({ salePrice })} whole />
        <label className="toggle-line">
          <Said id="sheet.hasSaleDate" />
          <input type="checkbox" role="switch" className="switch" checked={draft.hasSaleDate} onChange={(e) => set({ hasSaleDate: e.currentTarget.checked })} />
        </label>
        {draft.hasSaleDate && (
          <label className="sheet-row">
            <Said id="sheet.saleDate" />
            <input type="date" value={draft.saleDate} onChange={(e) => set({ saleDate: e.currentTarget.value })} />
          </label>
        )}
        <Said id="sheet.propertyFooter" as="p" className="sheet-foot" />
      </section>

      <section className="sheet-section">
        <Said id="sheet.characteristics" as="h3" />
        <button
          type="button"
          className="btn quiet lookup"
          data-held-copy={lookingUp ? "sheet.lookingUp" : "sheet.lookUp"}
          disabled={draft.address.trim() === "" || lookingUp}
          onClick={() => void lookUp()}
        >
          {say(lookingUp ? "sheet.lookingUp" : "sheet.lookUp")}
        </button>
        {lookupLine !== null && <p className="sheet-foot" data-copy-code={lookupLine.code} role="status">{lookupLine.text}</p>}
        <NumberRow labelId="sheet.sqft" unitId="sheet.sqftUnit" value={draft.sqft} onChange={(sqft) => set({ sqft })} />
        <NumberRow labelId="sheet.beds" value={draft.beds} onChange={(beds) => set({ beds })} whole />
        <NumberRow labelId="sheet.baths" value={draft.baths} onChange={(baths) => set({ baths })} />
        <NumberRow labelId="sheet.distance" unitId="sheet.miUnit" value={draft.distanceMi} onChange={(distanceMi) => set({ distanceMi })} />
      </section>

      {warnings.length > 0 && (
        <section className="sheet-section warnings" role="status">
          {warnings.map((w) => <p key={w} className="sheet-warning">{w}</p>)}
        </section>
      )}

      <section className="sheet-section">
        <label className="toggle-line">
          <Said id="sheet.include" />
          <input type="checkbox" role="switch" className="switch" checked={draft.included} onChange={(e) => set({ included: e.currentTarget.checked })} />
        </label>
        <Said id="sheet.includeFooter" as="p" className="sheet-foot" />
      </section>
    </Sheet>
  );
}

/** The app's `OptionalDecimalField`/`OptionalIntField`: empty is no figure, never a zero. */
function NumberRow({
  labelId, value, onChange, prefixId, unitId, whole = false,
}: {
  labelId: Parity3WordingId;
  value: number | null;
  onChange: (v: number | null) => void;
  prefixId?: Parity3WordingId;
  unitId?: Parity3WordingId;
  whole?: boolean;
}) {
  const [text, setText] = useState(value === null ? "" : String(value));
  useEffect(() => {
    setText((t) => (Number(t.replace(/[^0-9.]/g, "")) === value ? t : value === null ? "" : String(value)));
  }, [value]);
  return (
    <label className="sheet-row">
      <Said id={labelId} />
      <span className="sheet-input">
        {prefixId !== undefined && <span className="affix" data-held-copy={prefixId}>{say(prefixId)}</span>}
        <input
          aria-label={say(labelId)}
          type="text"
          inputMode={whole ? "numeric" : "decimal"}
          value={text}
          onChange={(e) => {
            const typed = e.currentTarget.value;
            setText(typed);
            const cleaned = typed.replace(/[^0-9.]/g, "");
            const n = cleaned === "" ? null : Number(cleaned);
            onChange(n === null || !Number.isFinite(n) ? null : whole ? Math.trunc(n) : n);
          }}
        />
        {unitId !== undefined && <span className="affix" data-held-copy={unitId}>{say(unitId)}</span>}
      </span>
    </label>
  );
}
